import { createSelector } from 'reselect';

import { StateDictionary } from './utils/state-manager';

export type ContactMessage = {
  dirty: boolean;
  loading: boolean;
  messages: Array<Message>;
  scheduledMessages: Array<ScheduledMessage>;
};
type ContactMessagesDictionary = { [contactId: number]: ContactMessage };
type ContactCallsDictionary = { [contactId: number]: Array<Call> };
type ScheduledMessageDictionary = { [scheduledMessageId: number]: ScheduledMessage };

export type ContactState = {
  messagesByContactId: ContactMessagesDictionary;
  callsByContactId: ContactCallsDictionary;
  callsByContactIdStatus: StateDictionary;
  scheduledMessagesById: ScheduledMessageDictionary;
  scheduledMessagesStateByContactId: StateDictionary;
};
type State = { contacts: ContactState };

export const callsSelector = (state: State, contactId: number) =>
  state.contacts.callsByContactId[contactId];
const callsByContactIdStateSelector = (state: State) => state.contacts.callsByContactIdStatus;

export const callsStateForContactIdSelector = createSelector(
  [callsByContactIdStateSelector, (_state: State, contactId: number) => contactId],
  (callsByContactId, contactId) => callsByContactId[contactId],
);

export const jobApplicantContextSelector = createSelector(
  (jobApplicantId) => jobApplicantId,
  (jobApplicantId) =>
    ({
      jobApplicantId,
    } as ContactContextType),
);

export const employeeContextSelector = createSelector(
  (employeeId) => employeeId,
  (employeeId) =>
    ({
      employeeId,
    } as ContactContextType),
);

const messagesByContactIdSelector = (state: State) => state.contacts.messagesByContactId;
export const messagesForContactIdSelector = createSelector(
  [messagesByContactIdSelector, (_state: State, contactId: number) => contactId],
  (messagesByContactId, contactId) => messagesByContactId[contactId]?.messages ?? null,
);
export const messagesWithImagesForContactIdSelector = createSelector(
  [messagesForContactIdSelector],
  (messagesForContactId) => messagesForContactId?.filter((m) => m.imageUrls) ?? null,
);

export const messagesForEmployeeIdSelector = createSelector(
  [
    messagesByContactIdSelector,
    (_state: State, contactId: number, employeeId: number) => ({ contactId, employeeId }),
  ],
  (messagesByContactId, ids) => {
    const contactMessages = messagesByContactId[ids.contactId];
    return (
      contactMessages?.messages?.filter((m) => m.activity.employeeId === ids.employeeId) ?? null
    );
  },
);
export const messagesWithImagesForEmployeeIdSelector = createSelector(
  [messagesForEmployeeIdSelector],
  (messagesForEmployeeId) => messagesForEmployeeId?.filter((m) => m.imageUrls) ?? null,
);

export const messagesForApplicantIdSelector = createSelector(
  [
    messagesByContactIdSelector,
    (_state: State, contactId: number, jobApplicantId: number) => ({ contactId, jobApplicantId }),
  ],
  (messagesByContactId, ids) => {
    const contactMessages = messagesByContactId[ids.contactId];
    return (
      contactMessages?.messages?.filter((m) => m.activity.jobApplicantId === ids.jobApplicantId) ??
      null
    );
  },
);
export const messagesWithImagesForApplicantIdSelector = createSelector(
  [messagesForApplicantIdSelector],
  (messagesForApplicantId) => messagesForApplicantId?.filter((m) => m.imageUrls) ?? null,
);

export const scheduledMessagesForEmployeeIdSelector = createSelector(
  [
    messagesByContactIdSelector,
    (_state: State, contactId: number, employeeId: number) => ({ contactId, employeeId }),
  ],
  (messagesByContactId, ids) => {
    const contactMessages = messagesByContactId[ids.contactId];
    return (
      contactMessages?.scheduledMessages?.filter((m) => m.employeeId === ids.employeeId) ?? null
    );
  },
);

const scheduledMessagesByIdSelector = (state: State) => state.contacts.scheduledMessagesById;
export const scheduledMessagesForContactIdSelector = createSelector(
  [scheduledMessagesByIdSelector, (_state: State, contactId: number) => contactId],
  (scheduledMessagesById, contactId) =>
    Object.values(scheduledMessagesById).filter((sm) => sm.contactId === contactId),
);

const scheduledMessagesStateByContactIdSelector = (state: State) =>
  state.contacts.scheduledMessagesStateByContactId;
export const scheduledMessagesStateForContactIdIdSelector = createSelector(
  [scheduledMessagesStateByContactIdSelector, (_state: State, contactId: number) => contactId],
  (scheduledMessagesStateByContactId, contactId) => scheduledMessagesStateByContactId[contactId],
);
